<template>
  <component
    :is="
      requiresContactDetails || requiredFields === 'none'
        ? FullscreenMessenger
        : Messenger
    "
    @locale-changed="handleLocaleChanged"
  >
    <template v-slot:header>
      <div
        class="flex flex-col items-center space-y-4 text-center px-12 relative"
      >
        <div class="absolute -top-32">
          <Vue3Lottie
            :loop="1"
            width="320px"
            animation-link="/images/animations/confetti-explode.json"
          />
        </div>
        <ThumbsUp class="w-12 h-auto text-white" />
        <h1 class="text-white text-2xl font-semibold">
          {{ t("components.messenger.success.title") }}
        </h1>
        <p class="text-white">
          {{ t("components.messenger.success.description") }}
        </p>
      </div>
    </template>

    <template v-slot:content>
      <div
        v-if="!requiresContactDetails && requiredFields !== 'none'"
        class="flex flex-col space-y-4 h-full"
      >
        <div
          v-if="!hasProvidedContactDetails"
          class="p-4 grow flex flex-col gap-4"
        >
          <Errors />

          <div class="text-sm font-medium text-gray-500">
            <p>
              {{ t("components.messenger.success.want_to_stay_updated") }}
            </p>
            <p>{{ t("components.messenger.success.leave_contact_details") }}</p>
          </div>

          <div class="flex flex-col">
            <div v-if="requireName">
              <label
                for="email"
                class="block text-sm font-medium text-gray-700"
              >
                {{ t("validation.attributes.name") }}</label
              >
              <div class="mt-1">
                <input
                  v-model="details.name"
                  type="text"
                  name="name"
                  id="name"
                  class="block w-full rounded-sm border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
                />
              </div>
            </div>

            <div class="flex items-center my-4" v-if="requireName">
              <div class="flex-grow h-px bg-gray-300"></div>
            </div>

            <div
              v-if="
                requiredFields === 'only_email' ||
                requiredFields === 'email_or_phone'
              "
            >
              <label
                for="email"
                class="block text-sm font-medium text-gray-700"
              >
                {{ t("validation.attributes.email") }}</label
              >
              <div class="mt-1">
                <input
                  v-model="details.email"
                  type="email"
                  name="email"
                  id="email"
                  class="block w-full rounded-sm border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
                />
              </div>
            </div>

            <div
              class="flex items-center mt-2"
              v-if="requiredFields === 'email_or_phone'"
            >
              <div class="flex-grow h-px bg-gray-300"></div>
              <span
                class="flex-shrink text-gray-500 px-4 italic font-light lowercase"
                >{{ t("components.or") }}</span
              >
              <div class="flex-grow h-px bg-gray-300"></div>
            </div>

            <div
              v-if="
                requiredFields === 'only_phone' ||
                requiredFields === 'email_or_phone'
              "
            >
              <label
                for="phone"
                class="block text-sm font-medium text-gray-700"
              >
                {{ t("validation.attributes.phone") }} </label
              ><phone-input
                class="mt-1"
                defaultCountry="BE"
                :value="`${details.phone.dialCode}${details.phone.nationalNumber}`"
                :allowed="[]"
                name="phone"
                required
                @phoneData="handlePhoneEntered"
              />
            </div>
          </div>
        </div>
        <div
          v-else
          class="grow flex flex-col items-center justify-center text-center"
        >
          <img src="/images/empty/comments.svg" class="mx-auto" />
          <p class="text-sm font-medium text-gray-500">
            {{ t("components.messenger.success.contact_success") }}
          </p>
        </div>

        <div
          class="p-4 flex flex-col gap-2 items-stretch border-t border-gray-200"
        >
          <template v-if="!hasProvidedContactDetails">
            <primary-button
              :label="t('components.buttons.send')"
              :loading="isLoading"
              @click="handleSubmitContactDetails"
              :disabled="
                (!details.email && !details.phone.number) ||
                (requireName && !details.name)
              "
            />
            <span
              class="cursor-pointer text-sm text-gray-600 hover:text-gray-500 font-semibold text-center"
              @click="handleReportAnotherProblem"
            >
              {{ t("components.messenger.report_new_problem") }}
            </span>
          </template>
          <template v-else>
            <primary-button
              :label="t('components.messenger.report_new_problem')"
              @click="handleReportAnotherProblem"
              class="text-gray-500 cursor-pointer"
            />
          </template>
        </div>
      </div>
      <div v-else>
        <div class="mt-6 flex justify-center">
          <primary-button
            :label="t('components.messenger.report_new_problem')"
            buttonStyle="secondary"
            @click="handleReportAnotherProblem"
          />
        </div>
      </div>
    </template>
  </component>
</template>

<script setup>
import FullscreenMessenger from "@/Tailwind/Layouts/FullscreenMessenger.vue";
import Messenger from "@/Tailwind/Layouts/Messenger.vue";
import { Vue3Lottie } from "vue3-lottie";
import ThumbsUp from "@/Tailwind/Icon/ThumbsUp.vue";
import PrimaryButton from "@/Tailwind/Components/Buttons/PrimaryButton.vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { usePage } from "@inertiajs/vue3";
import { ref } from "vue";
import { PhoneInput } from "@lbgm/phone-number-input";
import "@lbgm/phone-number-input/style";
import { useI18n } from "vue-i18n";
import Errors from "@/Tailwind/Components/Errors.vue";
import { useStorage } from "@vueuse/core";
import { useCookies } from "vue3-cookies";

const details = useStorage("messenger", {
  email: null,
  phone: {
    country: null,
    dialCode: null,
    nationalNumber: null,
    number: null,
  },
  name: null,
});

function handlePhoneEntered(data) {
  details.value.phone = {
    country: data.country,
    dialCode: data.dialCode,
    nationalNumber: data.nationalNumber,
    number: data.number,
  };
}

const { t } = useI18n();

const isLoading = ref(false);
const hasProvidedContactDetails = ref(false);

const tenant = usePage().props.tenant;
const locale = usePage().props.locale;
const problem = usePage().props.problem;
const messenger = usePage().props.problem.messenger;
const requiresContactDetails =
  usePage().props.tenant.settings.require_contact_details;
const requiredFields = usePage().props.tenant.settings.notification_options;
const requireName = usePage().props.tenant.settings.require_name;

const { cookies } = useCookies();

const currentLocale = ref(cookies.get("messenger_locale") || locale);

function handleLocaleChanged(locale) {
  currentLocale.value = locale;
}

function handleReportAnotherProblem() {
  router.visit(route("space.welcome", [tenant, problem.space_id]));
}

function handleSubmitContactDetails() {
  router.post(
    route("problem.report.contact.store", [tenant, problem, messenger]),
    {
      email: details.value.email,
      name: details.value.name,
      mobile: details.value.phone.number,
      country_code: details.value.phone.country,
    },
    {
      onStart: () => (isLoading.value = true),
      onFinish: () => (isLoading.value = false),
      onSuccess: () => (hasProvidedContactDetails.value = true),
    }
  );
}
</script>

<style lang="scss">
.baseinput-core {
  @apply bg-white w-full rounded-sm border border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm #{!important};

  input {
    @apply border-transparent focus:border-transparent focus:ring-0 text-sm;
  }
}

.lbgm-phone-scrll div {
  @apply hover:bg-gray-50;
}
</style>
